import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Terminal, Loader2, XCircle, RefreshCw, Pause, Play } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../../components/ui/dialog";
import { Button } from "../../../../components/ui/button";
import { ScrollArea } from "../../../../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { Badge } from "../../../../components/ui/badge";
import { Checkbox } from "../../../../components/ui/checkbox";
import Convert from 'ansi-to-html';

interface LogsDialogProps {
  open: boolean;
  podName: string;
  namespace: string;
  onClose: () => void;
}

interface ContainerLogEntry {
  containerName: string;
  timestamp: string;
  content: string;
  rawText: string;
}

const LogsDialog: React.FC<LogsDialogProps> = ({
  open,
  podName,
  namespace,
  onClose
}) => {
  const [logs, setLogs] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState<boolean>(true);
  const [containers, setContainers] = useState<string[]>([]);
  
  // Track container logs separately
  const [containerLogs, setContainerLogs] = useState<Record<string, string>>({});
  const [mergedLogs, setMergedLogs] = useState<ContainerLogEntry[]>([]);
  
  // Track enabled containers
  const [enabledContainers, setEnabledContainers] = useState<Record<string, boolean>>({});
  
  // Ref for the abort controller to cancel log streaming
  const abortControllerRefs = useRef<Record<string, AbortController>>({});
  // Ref for the scroll container div, not the ScrollArea component itself
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Create a converter instance
  const converter = useMemo(() => new Convert({
    colors: {
      0: '#000000',
      1: '#FF0000',
      2: '#00FF00',
      3: '#FFFF00',
      4: '#0000FF',
      5: '#FF00FF',
      6: '#00FFFF',
      7: '#FFFFFF',
      8: '#808080',
      9: '#FF0000',
      10: '#00FF00',
      11: '#FFFF00',
      12: '#0000FF',
      13: '#FF00FF',
      14: '#00FFFF',
      15: '#FFFFFF'
    },
    fg: '#FFFFFF',
    bg: '#000000',
    escapeXML: true,
    stream: false
  }), []);

  // Function to convert ANSI to HTML
  const convertAnsiToHtml = (text: string): string => {
    try {
      // Ensure proper line breaks before conversion
      const formattedText = text
        // Split by newlines or recognizable log patterns
        .split(/\n|(?=\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}(?:\.\d+)?)/g)
        // Filter empty lines
        .filter(line => line.trim())
        // Rejoin with explicit line breaks
        .join('\n');
      
      const html = converter.toHtml(formattedText);
      
      // Add CSS to ensure all text has the right color and formatting
      return `<style>
        .terminal-log { 
          color: #FFFFFF; 
          white-space: pre-wrap; 
          padding: 4px;
          line-height: 1.5;
        }
        .terminal-log span { 
          display: inline; 
        }
      </style>
      <div class="terminal-log">${html}</div>`;
    } catch (error) {
      console.error('Error converting ANSI to HTML:', error);
      return `<div style="color: white; white-space: pre-wrap; padding: 4px; line-height: 1.5;">${text}</div>`;
    }
  };

  // Function to format JSON in logs
  const formatLogText = (text: string): string => {
    try {
      // Look for JSON objects in the log text
      const formattedText = text.replace(/(\{.*?\})/g, (match) => {
        try {
          // Try to parse and format JSON
          const jsonObj = JSON.parse(match);
          return JSON.stringify(jsonObj, null, 2);
        } catch {
          // If it's not valid JSON, return as is
          return match;
        }
      });
      return formattedText;
    } catch {
      return text;
    }
  };

  // Function to encode pod name and namespace for URL safety
  const safeEncodeURIComponent = (str: string) => {
    // Encode but preserve slashes as is
    return encodeURIComponent(str).replace(/%2F/g, '/');
  };

  // Function to scroll to bottom
  const scrollToBottom = () => {
    if (scrollContainerRef.current && isStreaming) {
      const viewport = scrollContainerRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (viewport) {
        setTimeout(() => {
          viewport.scrollTop = viewport.scrollHeight;
        }, 0);
      }
    }
  };

  // Function to extract timestamp from a log line with better precision
  const extractTimestamp = (logLine: string): string => {
    // Look for ISO timestamps (yyyy-mm-dd hh:mm:ss.msZ format)
    const isoTimestampMatch = logLine.match(/^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z)?/);
    if (isoTimestampMatch) {
      return isoTimestampMatch[0];
    }
    
    // Look for standard timestamp format (yyyy-mm-dd hh:mm:ss)
    const standardTimestampMatch = logLine.match(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/);
    if (standardTimestampMatch) {
      return standardTimestampMatch[0];
    }
    
    // Look for timestamp format with milliseconds (yyyy-mm-ddThh:mm:ss.msZ)
    const msTimestampMatch = logLine.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+Z/);
    if (msTimestampMatch) {
      return msTimestampMatch[0];
    }
    
    // Return empty string if no timestamp found
    return '';
  };

  // Merge logs from different containers and sort by timestamp
  const mergeLogs = () => {
    // Skip merging if there are no logs
    if (Object.keys(containerLogs).length === 0) return;
    
    const entries: ContainerLogEntry[] = [];
    const processedLines = new Set<string>(); // Track processed lines to avoid duplicates
    
    // Process logs from each container
    Object.entries(containerLogs).forEach(([containerName, logs]) => {
      // Skip if container is disabled
      if (!enabledContainers[containerName]) return;
      
      // First, split by newlines to get initial line breaks
      const rawLines = logs.split('\n');
      
      // Process each raw line
      rawLines.forEach(rawLine => {
        if (!rawLine.trim()) return;
        
        // Further split lines that might contain multiple timestamp-prefixed entries
        const subLines = rawLine.split(/(?=\d{4}-\d{2}-\d{2}[\sT]\d{2}:\d{2}:\d{2})/g);
        
        subLines.forEach(line => {
          if (!line.trim()) return;
          
          // Create a unique key for the line to avoid duplicates
          const lineKey = `${containerName}:${line.trim()}`;
          if (processedLines.has(lineKey)) return;
          processedLines.add(lineKey);
          
          // Extract timestamp with better precision
          const timestamp = extractTimestamp(line);
          // If no timestamp found, generate one using the current time with an incremental offset
          const effectiveTimestamp = timestamp || new Date(Date.now() + entries.length).toISOString();
          
          entries.push({
            containerName,
            timestamp: effectiveTimestamp,
            content: line,
            rawText: line
          });
        });
      });
    });
    
    // Sort entries by timestamp with more precise comparison
    entries.sort((a, b) => {
      // First compare by timestamp string
      const timestampComparison = a.timestamp.localeCompare(b.timestamp);
      
      // If timestamps are equal (or missing), maintain the original order
      if (timestampComparison === 0) {
        return 0;
      }
      
      return timestampComparison;
    });
    
    setMergedLogs(entries);
  };

  // Function to fetch pod details and get container names
  const fetchPodDetails = async () => {
    if (!podName || !namespace) return;
    
    try {
      const url = `/api/v1/namespaces/${safeEncodeURIComponent(namespace)}/pods/${safeEncodeURIComponent(podName)}`;
      const response = await fetch(`http://localhost:8181${url}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch pod details: ${response.statusText} (${response.status})`);
      }
      
      const podDetails = await response.json();
      
      // Extract container names
      const containerNames = podDetails.spec.containers.map((container: any) => container.name);
      setContainers(containerNames);
      
      // Initialize enabled containers (all enabled by default)
      const initialEnabledState: Record<string, boolean> = {};
      containerNames.forEach(name => {
        initialEnabledState[name] = true;
      });
      setEnabledContainers(initialEnabledState);
      
      // Initialize container logs
      const initialLogs: Record<string, string> = {};
      containerNames.forEach(name => {
        initialLogs[name] = '';
      });
      setContainerLogs(initialLogs);
    } catch (err) {
      console.error('Failed to fetch pod details:', err);
      setError(`Failed to fetch pod details: ${err.message}`);
    }
  };

  // Function to load logs for a single container (non-streaming)
  const loadContainerLogs = async (containerName: string) => {
    if (!podName || !namespace || !containerName) return;
    
    try {
      // Use a more reliable approach to fetch logs
      const url = `/api/v1/namespaces/${safeEncodeURIComponent(namespace)}/pods/${safeEncodeURIComponent(podName)}/log?tailLines=1000&container=${safeEncodeURIComponent(containerName)}`;
      const response = await fetch(`http://localhost:8181${url}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch logs for ${containerName}: ${response.statusText} (${response.status})`);
      }
      
      const logsText = await response.text();
      
      setContainerLogs(prev => ({
        ...prev,
        [containerName]: logsText || ''
      }));
    } catch (err) {
      console.error(`Failed to fetch logs for ${containerName}:`, err);
      // Not setting global error, just log it
    }
  };

  // Function to load logs for all enabled containers
  const loadAllContainerLogs = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const enabledContainerNames = Object.entries(enabledContainers)
        .filter(([_, enabled]) => enabled)
        .map(([name]) => name);
      
      // Load logs for each enabled container
      await Promise.all(
        enabledContainerNames.map(name => loadContainerLogs(name))
      );
      
      mergeLogs();
    } catch (err) {
      console.error('Failed to fetch pod logs:', err);
      setError(`Failed to fetch logs: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to setup log streaming for a single container
  const streamContainerLogs = async (containerName: string) => {
    if (!podName || !namespace || !containerName) return;
    
    // Clean up any existing stream for this container
    if (abortControllerRefs.current[containerName]) {
      abortControllerRefs.current[containerName].abort();
      delete abortControllerRefs.current[containerName];
    }
    
    try {
      // Create a new abort controller
      const controller = new AbortController();
      abortControllerRefs.current[containerName] = controller;
      
      // Construct URL directly with proper encoding
      const url = `/api/v1/namespaces/${safeEncodeURIComponent(namespace)}/pods/${safeEncodeURIComponent(podName)}/log?follow=true&tailLines=100&container=${safeEncodeURIComponent(containerName)}`;
      const response = await fetch(`http://localhost:8181${url}`, {
        signal: controller.signal
      });
      
      if (!response.ok) {
        throw new Error(`Failed to stream logs for ${containerName}: ${response.statusText} (${response.status})`);
      }
      
      const reader = response.body!.getReader();
      const decoder = new TextDecoder('utf-8');
      
      // Process the stream
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          console.log(`Log stream for ${containerName} closed`);
          break;
        }
        
        // Decode the received chunk
        const chunk = decoder.decode(value, { stream: true });
        
        // Update the container logs - use a callback to ensure we have the latest state
        setContainerLogs(prev => {
          const prevLogs = prev[containerName] || '';
          const newLogs = prevLogs + chunk;
          
          // Return new object with updated logs for this container
          return {
            ...prev,
            [containerName]: newLogs
          };
        });
      }
    } catch (err) {
      // Only log error if the abort wasn't intentional
      if (err.name !== 'AbortError') {
        console.error(`Failed to stream logs for ${containerName}:`, err);
      }
    }
  };

  // Function to streamAllContainerLogs - update to add auto-scrolling
  const streamAllContainerLogs = async () => {
    setIsLoading(true);
    
    try {
      const enabledContainerNames = Object.entries(enabledContainers)
        .filter(([_, enabled]) => enabled)
        .map(([name]) => name);
      
      // Start streaming for each enabled container
      enabledContainerNames.forEach(name => {
        streamContainerLogs(name);
      });
      
      // Set up an interval to auto-scroll if streaming is enabled
      const scrollInterval = setInterval(() => {
        if (isStreaming) {
          scrollToBottom();
        }
      }, 500);
      
      // Store the interval ID for cleanup
      return () => clearInterval(scrollInterval);
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle streaming on/off
  const toggleStreaming = () => {
    setIsStreaming(!isStreaming);
  };

  // Handle refreshing logs - restart the stream
  const handleRefresh = () => {
    // Clear previous logs and errors
    const resetLogs: Record<string, string> = {};
    containers.forEach(name => {
      resetLogs[name] = '';
    });
    setContainerLogs(resetLogs);
    setMergedLogs([]);
    setError(null);
    
    // Restart streaming
    Object.values(abortControllerRefs.current).forEach(controller => {
      controller.abort();
    });
    abortControllerRefs.current = {};
    
    // Reload logs for all enabled containers
    loadAllContainerLogs();
    streamAllContainerLogs();
  };

  // Handle container toggle
  const handleContainerToggle = (containerName: string, isEnabled: boolean) => {
    setEnabledContainers(prev => ({
      ...prev,
      [containerName]: isEnabled
    }));
    
    // If enabling a previously disabled container, start streaming it
    if (isEnabled && !abortControllerRefs.current[containerName]) {
      loadContainerLogs(containerName).then(() => {
        streamContainerLogs(containerName);
        mergeLogs();
      });
    }
    
    // If disabling a container, stop its stream
    if (!isEnabled && abortControllerRefs.current[containerName]) {
      abortControllerRefs.current[containerName].abort();
      delete abortControllerRefs.current[containerName];
      mergeLogs(); // Refresh the merged logs
    }
  };

  // Effect to fetch pod details when dialog opens
  useEffect(() => {
    if (open && podName && namespace) {
      fetchPodDetails();
    }
  }, [open, podName, namespace]);

  // Effect to start/stop streaming when containers are loaded
  useEffect(() => {
    if (open && podName && namespace && containers.length > 0) {
      // First load logs for immediate feedback
      loadAllContainerLogs();
      // Then set up streaming for real-time updates
      streamAllContainerLogs();
    }
    
    // Cleanup function to close all streams when component unmounts or dialog closes
    return () => {
      Object.values(abortControllerRefs.current).forEach(controller => {
        controller.abort();
      });
      abortControllerRefs.current = {};
      
      // Clear logs when dialog closes
      if (!open) {
        setContainerLogs({});
        setMergedLogs([]);
        setError(null);
        setContainers([]);
        setEnabledContainers({});
      }
    };
  }, [open, podName, namespace, containers.length]);

  // Effect to merge logs when container logs change
  useEffect(() => {
    // Batch the updates to avoid flickering
    const timeoutId = setTimeout(() => {
      mergeLogs();
    }, 50); // Small delay to batch multiple container log updates
    
    return () => clearTimeout(timeoutId);
  }, [containerLogs, enabledContainers]);

  // Helper function to get a unique color for each container
  const getContainerColor = (containerName: string): string => {
    const colors = [
      '#2563eb', // blue
      '#dc2626', // red
      '#059669', // green
      '#d97706', // amber
      '#7c3aed', // purple
      '#db2777', // pink
      '#0891b2', // cyan
      '#4b5563', // gray
      '#8b5cf6', // violet
    ];
    
    // Generate a consistent index for a container name
    const index = containerName.split('').reduce((acc, char) => {
      return acc + char.charCodeAt(0);
    }, 0) % colors.length;
    
    return colors[index];
  };

  // Convert merged logs to HTML with terminal colors
  const htmlLogs = useMemo(() => {
    // If no logs yet, return empty
    if (mergedLogs.length === 0) {
      return '';
    }

    // Always show container badges, even for single container
    const logHtml = mergedLogs.map((entry, index) => {
      const containerBadgeColor = getContainerColor(entry.containerName);
      const formattedContent = formatLogText(entry.content);
      const convertedContent = converter.toHtml(formattedContent);
      
      return `<div class="log-entry" key="${index}">
        <span class="container-badge" style="
          display: inline-block;
          padding: 1px 6px;
          margin-right: 6px;
          border-radius: 3px;
          font-size: 10px;
          background-color: ${containerBadgeColor};
          color: white;
        ">${entry.containerName}</span>
        <span class="log-content">${convertedContent}</span>
      </div>`;
    }).join('');
    
    return `<style>
      .log-container {
        color: #FFFFFF;
        white-space: pre-wrap;
        padding: 2px;
        line-height: 1.3;
      }
      .log-entry {
        margin-bottom: 1px;
        display: flex;
        align-items: flex-start;
      }
      .log-content {
        flex: 1;
      }
      .log-content span {
        display: inline;
      }
    </style>
    <div class="log-container">${logHtml}</div>`;
  }, [mergedLogs, containers.length]);

  // Return empty div for container toggles if there's only one container
  const renderContainerToggles = () => {
    if (containers.length <= 1) {
      return null;
    }
    
    return (
      <div className="mb-4 flex-shrink-0">
        <label className="text-sm font-medium mb-1 block">Containers:</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
          {containers.map(container => (
            <div key={container} className="flex items-center space-x-2">
              <Checkbox 
                id={`container-${container}`}
                checked={!!enabledContainers[container]}
                onCheckedChange={(checked) => handleContainerToggle(container, !!checked)}
              />
              <label 
                htmlFor={`container-${container}`}
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center"
              >
                <div
                  className="w-2 h-2 rounded-full mr-1.5"
                  style={{ backgroundColor: getContainerColor(container) }}
                ></div>
                {container}
              </label>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[800px] h-[80vh] max-h-[80vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center">
            <Terminal className="h-5 w-5 mr-2" />
            Pod Logs: <span className="font-mono text-xs ml-2">{podName}</span>
          </DialogTitle>
          <DialogDescription>
            Log stream from the pod in namespace {namespace}
          </DialogDescription>
        </DialogHeader>
        
        {renderContainerToggles()}
        
        <div 
          className="flex-grow min-h-0 bg-zinc-950 rounded-md p-4 my-4 flex flex-col overflow-hidden" 
          ref={scrollContainerRef}
        >
          <ScrollArea className="h-full overflow-auto">
            {isLoading && mergedLogs.length === 0 ? (
              <div className="flex justify-center items-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Loading logs...</span>
              </div>
            ) : error ? (
              <div className="flex flex-col justify-center items-center h-full text-destructive">
                <XCircle className="h-8 w-8 mb-2" />
                <span>{error}</span>
              </div>
            ) : mergedLogs.length > 0 ? (
              <div 
                className="h-full font-mono text-xs text-white min-w-full p-2" 
                style={{ backgroundColor: "#000" }}
                dangerouslySetInnerHTML={{ __html: htmlLogs }}
              />
            ) : (
              <div className="flex justify-center items-center h-full text-muted-foreground">
                No logs available for this pod
              </div>
            )}
          </ScrollArea>
        </div>
        
        <DialogFooter className="flex justify-between flex-shrink-0">
          <div className="flex space-x-2">
            <Button variant="outline" onClick={handleRefresh} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              variant={isStreaming ? "default" : "outline"} 
              onClick={toggleStreaming}
              title={isStreaming ? "Pause auto-scrolling" : "Resume auto-scrolling"}
            >
              {isStreaming ? (
                <>
                  <Pause className="h-4 w-4 mr-2" />
                  Pause
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Resume
                </>
              )}
            </Button>
          </div>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LogsDialog; 